/**
 * Shared RFP data fetching hooks
 *
 * These hooks provide React Query integration for common RFP API endpoints.
 * They support data transformation via the `select` prop for flexible usage.
 */

import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { toMinutes, useApi } from "@platform/api";

import { RfpCarriersResponse, RfpClientDetailsResponse } from "./types";

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query keys for RFP data caching
 *
 * These keys are shared across broker-portal-fe and coverage-overview
 * to ensure cache sharing when components from both libraries are used together.
 */
export const rfpQueryKeys = {
  /** Key for RFP carriers query */
  carriers: (rfpId: string): ["rfp", string, "carriers", "contacts"] => [
    "rfp",
    rfpId,
    "carriers",
    "contacts",
  ],
  /** Key for RFP client details query */
  clientDetails: (rfpId: string): ["rfps", string, "client"] => [
    "rfps",
    rfpId,
    "client",
  ],
};

// ============================================================================
// Carrier Hooks
// ============================================================================

interface UseRfpCarriersOptions<TSelected> {
  rfpId: string;
  enabled?: boolean;
  queryKey?: ["rfp", string, "carriers", "contacts"];
  select?: (data: RfpCarriersResponse) => TSelected;
  onSuccess?: (data: RfpCarriersResponse) => void;
}

/**
 * Hook to fetch RFP carriers
 *
 * Returns carriers grouped by connection status (connected, unconnected, unknown).
 * Use the `select` prop to transform data when reading from cache.
 *
 * @param options.rfpId - The broker portal RFP ID to fetch carriers for
 * @param options.enabled - Whether the query should execute (default: true)
 * @param options.queryKey - Custom query key (optional)
 * @param options.select - Transform function applied on cache read
 * @param options.onSuccess - Callback on successful fetch
 * @returns Query result with carrier data
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { data } = useRfpCarriers({ rfpId: "123" });
 *
 * // With transformation
 * const { data: carrierNames } = useRfpCarriers({
 *   rfpId: "123",
 *   select: (data) => data.connected?.map(c => c.name) ?? []
 * });
 * ```
 */
export function useRfpCarriers<TSelected = RfpCarriersResponse>({
  rfpId,
  enabled = true,
  queryKey,
  select,
  onSuccess,
}: UseRfpCarriersOptions<TSelected>): UseQueryResult<TSelected, Error> {
  const axios = useApi();

  return useQuery<RfpCarriersResponse, Error, TSelected>({
    queryKey: queryKey ?? rfpQueryKeys.carriers(rfpId),
    enabled: enabled && !!rfpId,
    select,
    queryFn: () =>
      axios
        .get<RfpCarriersResponse>(`brokerportal/v1/rfp/${rfpId}/carriers`)
        .then((res) => {
          onSuccess?.(res.data);
          return res.data;
        }),
    staleTime: toMinutes(5),
  });
}

// ============================================================================
// Client Details Hooks
// ============================================================================

interface UseRfpClientDetailsOptions<TSelected> {
  rfpId: string;
  enabled?: boolean;
  select?: (data: RfpClientDetailsResponse) => TSelected;
}

// Overload: with selector
export function useRfpClientDetails<TSelected>(options: {
  rfpId: string;
  enabled?: boolean;
  select: (data: RfpClientDetailsResponse) => TSelected;
}): UseQueryResult<TSelected, Error>;

// Overload: without selector (returns full response)
export function useRfpClientDetails(options: {
  rfpId: string;
  enabled?: boolean;
}): UseQueryResult<RfpClientDetailsResponse, Error>;

/**
 * Hook to fetch RFP client details
 *
 * Returns client information including name, address, employee count, etc.
 * Use the `select` prop to transform data when reading from cache.
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.enabled - Whether the query should execute (default: true)
 * @param options.select - Transform function applied on cache read
 * @returns Query result with client details
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { data: client } = useRfpClientDetails({ rfpId: "123" });
 *
 * // With transformation
 * const { data: clientName } = useRfpClientDetails({
 *   rfpId: "123",
 *   select: (data) => data.name
 * });
 * ```
 */
export function useRfpClientDetails<TSelected = RfpClientDetailsResponse>({
  rfpId,
  enabled = true,
  select,
}: UseRfpClientDetailsOptions<TSelected>): UseQueryResult<TSelected, Error> {
  const axios = useApi();

  return useQuery<RfpClientDetailsResponse, Error, TSelected>({
    queryKey: rfpQueryKeys.clientDetails(rfpId),
    enabled: enabled && !!rfpId,
    select,
    queryFn: () =>
      axios
        .get<RfpClientDetailsResponse>(`brokerportal/v1/rfp/${rfpId}/client`)
        .then((res) => res.data),
    staleTime: toMinutes(5),
  });
}
